import {
  Building2,
  AlertCircle,
  TrendingUp,
  Users,
  Activity,
  Database,
  Zap,
  ArrowUp,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { usePlatformAdminAuthStore } from '../../../stores/platform-admin-auth.store';
import { useDashboard } from '../../../hooks/platform-admin/useDashboard';

export default function PlatformDashboard() {
  const admin = usePlatformAdminAuthStore((state) => state.admin);
  const { stats, loading, error } = useDashboard();

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatBytes = (bytes: number): string => {
    if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(2) + ' GB';
    if (bytes >= 1048576) return (bytes / 1048576).toFixed(2) + ' MB';
    if (bytes >= 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return bytes + ' B';
  };

  if (loading) {
    return (
      <div className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-96">
          <div className="text-gray-500">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-96">
          <div className="text-red-500">Failed to load dashboard: {error}</div>
        </div>
      </div>
    );
  }

  const mainStats = [
    {
      name: 'Total Organizations',
      value: stats.organizations.total.toString(),
      subtext: `${stats.organizations.active} active, ${stats.organizations.paused} paused`,
      icon: Building2,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      change: stats.recentActivity.newOrganizations7d,
      changeLabel: 'new this week',
    },
    {
      name: 'Active Users',
      value: formatNumber(stats.users.active),
      subtext: `${stats.users.total} total users`,
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      change: stats.users.last24Hours,
      changeLabel: 'new today',
    },
    {
      name: 'System Events (24h)',
      value: stats.systemEvents.total24Hours.toString(),
      subtext: `${stats.systemEvents.pending} pending, ${stats.systemEvents.critical} critical`,
      icon: AlertCircle,
      color: stats.systemEvents.critical > 0 ? 'text-red-600' : 'text-orange-600',
      bgColor: stats.systemEvents.critical > 0 ? 'bg-red-100' : 'bg-orange-100',
      change: stats.recentActivity.resolvedEvents7d,
      changeLabel: 'resolved this week',
    },
    {
      name: 'API Calls (24h)',
      value: formatNumber(stats.usage.apiCalls24h),
      subtext: `${stats.usage.activeIntegrations} active integrations`,
      icon: TrendingUp,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      change: null,
      changeLabel: '',
    },
  ];

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome, {admin?.firstName}!
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          Platform Admin Dashboard - Manage organizations and monitor system health
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {mainStats.map((stat) => (
          <Card key={stat.name}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className={`flex-shrink-0 p-3 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} />
                </div>
                {stat.change !== null && stat.change > 0 && (
                  <div className="flex items-center text-xs text-green-600">
                    <ArrowUp className="h-3 w-3 mr-1" />
                    +{stat.change}
                  </div>
                )}
              </div>
              <p className="text-sm font-medium text-gray-600">{stat.name}</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{stat.value}</p>
              {stat.subtext && (
                <p className="text-xs text-gray-500 mt-1">{stat.subtext}</p>
              )}
              {stat.change !== null && stat.changeLabel && (
                <p className="text-xs text-gray-400 mt-1">{stat.changeLabel}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Additional Stats Row */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0 p-3 rounded-lg bg-indigo-100">
                  <Database className="h-6 w-6 text-indigo-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Storage</p>
                  <p className="text-xl font-semibold text-gray-900">
                    {formatBytes(stats.usage.totalStorage)}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0 p-3 rounded-lg bg-yellow-100">
                  <Activity className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">New Users (7d)</p>
                  <p className="text-xl font-semibold text-gray-900">
                    {stats.recentActivity.newUsers7d}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0 p-3 rounded-lg bg-teal-100">
                  <Zap className="h-6 w-6 text-teal-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Trial Orgs</p>
                  <p className="text-xl font-semibold text-gray-900">
                    {stats.organizations.trial}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <a
                href="/platform-admin/organizations"
                className="block p-4 border border-gray-200 rounded-lg hover:bg-orange-50 hover:border-orange-300 transition-colors"
              >
                <div className="flex items-center">
                  <Building2 className="h-5 w-5 text-orange-600 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">Manage Organizations</p>
                    <p className="text-sm text-gray-600">Create, pause, or delete organizations</p>
                  </div>
                </div>
              </a>
              <a
                href="/platform-admin/events"
                className="block p-4 border border-gray-200 rounded-lg hover:bg-orange-50 hover:border-orange-300 transition-colors"
              >
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-orange-600 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">View System Events</p>
                    <p className="text-sm text-gray-600">Monitor and resolve errors</p>
                  </div>
                </div>
              </a>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>System Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <span className="text-sm font-medium text-green-800">API Server</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Operational
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <span className="text-sm font-medium text-green-800">Database</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Operational
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <span className="text-sm font-medium text-green-800">Retry Service</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Running
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Event Severity Breakdown */}
      {stats.systemEvents.bySeverity.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Events by Severity (24h)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.systemEvents.bySeverity.map((item) => {
                const severityColors = {
                  CRITICAL: 'bg-red-500',
                  HIGH: 'bg-orange-500',
                  MEDIUM: 'bg-yellow-500',
                  LOW: 'bg-blue-500',
                };
                const color = severityColors[item.severity as keyof typeof severityColors] || 'bg-gray-500';
                const total = stats.systemEvents.total24Hours;
                const percentage = total > 0 ? (item.count / total) * 100 : 0;

                return (
                  <div key={item.severity}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{item.severity}</span>
                      <span className="text-sm text-gray-600">{item.count}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`${color} h-2 rounded-full transition-all`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
